import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fileTypeSortingColumns } from 'types'
import z from 'zod'

import { queryFileTypeList } from '@api/file-type/file-type.queries'

import SearchBar from '@/components/search-bar'
import ensureUserRoleOnRoute from '@/lib/ensure-user-role-on-route'
import { paginationSchema, sortingSchema } from '@/lib/table'

import SectionHeader from '../../-components/section-layout/section-header'
import FileTypeArchiveDialog from './-components/file-type-archive-dialog'
import FileTypeCreateDialog from './-components/file-type-create-dialog'
import FileTypePatchDialog from './-components/file-type-patch-dialog'
import FileTypesTable from './-components/file-types-table'

const searchSchema = z.object({
  pagination: paginationSchema,
  sorting: sortingSchema(fileTypeSortingColumns),
  filters: z.object({ search: z.string().optional() }).optional(),
})

export const Route = createFileRoute('/app/settings/file-types')({
  component: RouteComponent,
  validateSearch: searchSchema,
  beforeLoad: async ({ context }) => {
    // Route requires to be an admin
    await ensureUserRoleOnRoute(context.queryClient)
  },
  loaderDeps: ({ search }) => search,
  loader: ({
    context: { queryClient },
    deps: { pagination, filters, sorting },
  }) => {
    queryClient.prefetchQuery(
      queryFileTypeList({
        ...pagination,
        pageIndex: pagination.pageIndex + 1,
        filters,
        sorting,
      }),
    )
  },
})

function RouteComponent() {
  const [archiveUuid, setArchiveUuid] = useState<string | null>(null)
  const [patchUuid, setPatchUuid] = useState<string | null>(null)
  const { filters } = Route.useSearch()
  const { t } = useTranslation()
  const navigate = useNavigate({ from: Route.fullPath })

  const handlePatchOpenChange = (open: boolean) =>
    setPatchUuid(open ? patchUuid : null)

  const handleArchiveOpenChange = (open: boolean) =>
    setArchiveUuid(open ? archiveUuid : null)

  return (
    <div className="flex h-full grow flex-col divide-y overflow-hidden">
      <SectionHeader isSecondary title={t('settings.file-types.title')}>
        <div className="flex items-center gap-2">
          <SearchBar
            tabIndex={1}
            className="max-w-62.75"
            text={filters?.search}
            setText={(search) =>
              navigate({
                to: '.',
                search: (previous) => ({
                  ...previous,
                  filters: { ...previous.filters, search },
                }),
              })
            }
          />
          <FileTypeCreateDialog />
        </div>
      </SectionHeader>
      <FileTypesTable onArchive={setArchiveUuid} onPatch={setPatchUuid} />
      <FileTypePatchDialog
        uuid={patchUuid ?? ''}
        open={!!patchUuid}
        onOpenChange={handlePatchOpenChange}
      />
      <FileTypeArchiveDialog
        uuid={archiveUuid ?? ''}
        open={!!archiveUuid}
        onOpenChange={handleArchiveOpenChange}
      />
    </div>
  )
}
